/**
 * Pastilles de COULEUR pour les axes de variantes d'un produit boutique.
 *
 * ⚠️ CONTRAINTE DE DONNÉES — un `VariantAxis` ne stocke QUE des noms :
 * `{ name: "Couleur", values: ["Noir", "Blanc"] }` (`packages/types/src/shop.ts`).
 * Il n'existe AUCUN champ hexadécimal en base, et en ajouter un obligerait
 * chaque association à saisir un code couleur — ce qu'aucune ne fera. On DÉDUIT
 * donc la couleur d'affichage du nom saisi, avec une règle stricte : si le nom
 * n'est pas reconnu, on ne l'invente PAS (voir « Règle de repli » ci-dessous).
 *
 * ─── Règle de repli (documentée, volontairement conservatrice) ───────────────
 * La bascule en pastilles se décide POUR L'AXE ENTIER, jamais valeur par
 * valeur. Un axe mi-pastilles mi-boutons serait illisible : l'acheteur ne
 * saurait pas si « Édition limitée » est une couleur qu'on n'a pas su afficher
 * ou une option d'une autre nature. Donc :
 *
 *   1. le NOM de l'axe doit désigner une couleur (« Couleur », « Color »,
 *      « Coloris », « Colour », « Teinte », « Coloration ») — casse et accents
 *      ignorés ; et
 *   2. TOUTES les valeurs de l'axe doivent être reconnues par le dictionnaire.
 *
 * Le seuil est 100 %, pas une « large majorité » : sur les axes réels (2 à 6
 * valeurs), tolérer une valeur inconnue reviendrait à afficher un axe à 66 %
 * pastilles, c'est-à-dire un axe visuellement incohérent. Une seule valeur non
 * reconnue → l'axe ENTIER retombe sur le rendu texte rectangulaire actuel, qui
 * reste parfaitement fonctionnel. Aucune régression possible, aucune couleur
 * inventée.
 *
 * ─── Contraste ──────────────────────────────────────────────────────────────
 * `swatchInk()` calcule la luminance relative (WCAG) de l'aplat pour choisir
 * une coche noire ou blanche, plutôt que de coder « blanc » en dur (invisible
 * sur une pastille jaune ou blanche). `needsOutline()` signale les aplats trop
 * clairs pour se détacher d'un fond blanc : ils reçoivent une bordure marquée.
 *
 * Règle projet `ui_no_bg_transparency` : toutes les valeurs ci-dessous sont des
 * hex OPAQUES à 6 chiffres — aucun `rgba()`, aucun canal alpha.
 */

/** Couleur d'affichage d'une valeur d'axe. */
export interface ShopColorSwatch {
  /** Aplat principal (hex opaque). */
  hex: string;
  /**
   * Second aplat pour les valeurs bicolores (« noir et blanc »). `null` pour
   * une couleur unie. Rendu en demi-pastille, jamais en dégradé translucide.
   */
  hex2: string | null;
}

/**
 * Dictionnaire FR (+ quelques équivalents EN courants sur les boutiques
 * associatives). Les clés sont NORMALISÉES : minuscules, sans accent, sans
 * ponctuation, espaces simples.
 *
 * Les teintes retenues sont des tons « textile » plutôt que les couleurs web
 * pures : un t-shirt « rouge » n'est jamais `#ff0000`.
 */
const COLOR_DICTIONARY: Record<string, string> = {
  // Neutres
  noir: "#111111",
  black: "#111111",
  blanc: "#ffffff",
  white: "#ffffff",
  "blanc casse": "#f2ede3",
  ecru: "#f0e9db",
  creme: "#f5eeda",
  ivoire: "#fffbeb",
  gris: "#9096a1",
  grey: "#9096a1",
  gray: "#9096a1",
  "gris clair": "#c9ced6",
  "gris fonce": "#565c66",
  anthracite: "#3a3f47",
  ardoise: "#4a5568",
  taupe: "#8b7d6b",
  argent: "#c0c4c9",
  argente: "#c0c4c9",
  silver: "#c0c4c9",
  or: "#c9a227",
  dore: "#c9a227",
  gold: "#c9a227",
  bronze: "#a1662f",
  cuivre: "#b06a3b",

  // Rouges / roses
  rouge: "#c62828",
  red: "#c62828",
  "rouge fonce": "#8e1b1b",
  bordeaux: "#6d1f2c",
  burgundy: "#6d1f2c",
  grenat: "#7b1f2b",
  brique: "#a24b34",
  corail: "#f26b5b",
  rose: "#e879a8",
  pink: "#e879a8",
  "rose pale": "#f6c6d8",
  "rose poudre": "#efc8cd",
  fuchsia: "#c2185b",
  magenta: "#b8256f",
  saumon: "#f0917b",

  // Oranges / jaunes
  orange: "#ef6c22",
  "orange fonce": "#c9531a",
  abricot: "#f2a65a",
  peche: "#f6b99e",
  jaune: "#f2c230",
  yellow: "#f2c230",
  "jaune pale": "#f7e08c",
  moutarde: "#c99a20",
  ocre: "#c08a2e",
  safran: "#e0a020",

  // Verts
  vert: "#2e7d4f",
  green: "#2e7d4f",
  "vert clair": "#7bc47f",
  "vert fonce": "#1b5e35",
  "vert sapin": "#14532d",
  "vert olive": "#6b7233",
  olive: "#6b7233",
  "vert kaki": "#5d6142",
  kaki: "#5d6142",
  khaki: "#5d6142",
  "vert menthe": "#8fd6b4",
  menthe: "#8fd6b4",
  "vert amande": "#a8c69f",
  emeraude: "#0f766e",
  turquoise: "#1eb3ac",
  "vert deau": "#a8ddd4",
  lime: "#a4c93a",
  anis: "#c3d94e",

  // Bleus
  bleu: "#1f6fd0",
  blue: "#1f6fd0",
  "bleu clair": "#7bb8ef",
  "bleu ciel": "#8ecae6",
  ciel: "#8ecae6",
  "bleu fonce": "#153a72",
  "bleu marine": "#1b2a53",
  marine: "#1b2a53",
  navy: "#1b2a53",
  "bleu roi": "#1e4fbd",
  "bleu nuit": "#161f3d",
  "bleu petrole": "#1c5a68",
  petrole: "#1c5a68",
  indigo: "#39408f",
  cyan: "#22a6b8",
  azur: "#3d8fe0",
  denim: "#3b5f8a",

  // Violets
  violet: "#6d3fa5",
  purple: "#6d3fa5",
  "violet clair": "#a78bda",
  mauve: "#a98cc4",
  lilas: "#c3aede",
  lavande: "#b8afd9",
  prune: "#5c2a4d",
  aubergine: "#452139",
  parme: "#c2b0d6",

  // Bruns / beiges
  marron: "#6b4429",
  brown: "#6b4429",
  "marron clair": "#96694a",
  "marron fonce": "#4a2e1c",
  chocolat: "#4b2e21",
  cafe: "#5b4034",
  camel: "#b8894f",
  caramel: "#b1712f",
  beige: "#ddcdb4",
  sable: "#e0d3b3",
  "beige clair": "#eadfca",
  chataigne: "#7a4a2b",
  cognac: "#94502a",
};

/** Valeurs BICOLORES : deux aplats pleins, jamais un dégradé. */
const BICOLOR_DICTIONARY: Record<string, [string, string]> = {
  "noir et blanc": ["#111111", "#ffffff"],
  "blanc et noir": ["#ffffff", "#111111"],
  "noir blanc": ["#111111", "#ffffff"],
  "bleu et blanc": ["#1f6fd0", "#ffffff"],
  "rouge et blanc": ["#c62828", "#ffffff"],
  "vert et blanc": ["#2e7d4f", "#ffffff"],
  "noir et rouge": ["#111111", "#c62828"],
  "noir et or": ["#111111", "#c9a227"],
  bicolore: ["#111111", "#ffffff"],
};

/** Noms d'axe qui désignent une couleur (normalisés). */
const COLOR_AXIS_NAMES = new Set([
  "couleur",
  "couleurs",
  "color",
  "colors",
  "colour",
  "colours",
  "coloris",
  "teinte",
  "teintes",
  "coloration",
]);

/**
 * Normalise une chaîne pour la recherche : minuscules, accents retirés,
 * ponctuation/tirets ramenés à des espaces, espaces compressés.
 *
 * « Bleu-Marine », « BLEU MARINE », « Bleu marine » → `bleu marine`.
 */
function normalize(input: string): string {
  return input
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

/** Mots à ignorer dans un nom composé (« bleu très clair », « gris chiné »). */
const IGNORED_WORDS = new Set(["tres", "un", "de", "du", "la", "le", "chine", "chinee"]);

/**
 * Couleur d'affichage d'une valeur, ou `null` si le nom n'est PAS reconnu.
 *
 * Ordre de résolution — du plus spécifique au plus général, pour que
 * « bleu marine » ne soit jamais rendu comme un bleu générique :
 *  1. bicolore exact (« noir et blanc ») ;
 *  2. nom composé exact (« bleu marine », « vert kaki », « gris clair ») ;
 *  3. nom composé après retrait des mots vides (« bleu très clair ») ;
 *  4. paire de mots adjacents reconnue dans une expression plus longue
 *     (« t-shirt bleu marine » → « bleu marine ») ;
 *  5. mot unique reconnu (« noir », « rouge »).
 * Aucun repli au-delà : un nom inconnu renvoie `null`.
 */
export function resolveShopColorSwatch(value: string): ShopColorSwatch | null {
  const key = normalize(value);
  if (!key) return null;

  const bicolor = BICOLOR_DICTIONARY[key];
  if (bicolor) return { hex: bicolor[0], hex2: bicolor[1] };

  const exact = COLOR_DICTIONARY[key];
  if (exact) return { hex: exact, hex2: null };

  const words = key.split(" ").filter((w) => !IGNORED_WORDS.has(w));
  if (words.length === 0) return null;

  const cleaned = words.join(" ");
  const cleanedHit = COLOR_DICTIONARY[cleaned];
  if (cleanedHit) return { hex: cleanedHit, hex2: null };

  // Paires adjacentes (« bleu marine » dans « super bleu marine »).
  for (let i = 0; i < words.length - 1; i += 1) {
    const pair = COLOR_DICTIONARY[`${words[i]} ${words[i + 1]}`];
    if (pair) return { hex: pair, hex2: null };
  }

  // Mot unique : on ne teste QUE des valeurs courtes (1 ou 2 mots) pour ne pas
  // transformer une phrase entière en couleur sur la foi d'un seul mot.
  if (words.length <= 2) {
    for (const word of words) {
      const single = COLOR_DICTIONARY[word];
      if (single) return { hex: single, hex2: null };
    }
  }

  return null;
}

/** Le nom de l'axe désigne-t-il une couleur ? (casse/accents ignorés) */
export function isColorAxisName(axisName: string): boolean {
  return COLOR_AXIS_NAMES.has(normalize(axisName));
}

/**
 * L'axe doit-il être rendu en PASTILLES ?
 *
 * Exige le nom d'axe couleur ET 100 % des valeurs reconnues (cf. « Règle de
 * repli » en tête de fichier). Un axe vide reste en rendu texte.
 */
export function shouldRenderAsColorSwatches(axis: {
  name: string;
  values: string[];
}): boolean {
  if (!isColorAxisName(axis.name)) return false;
  if (!axis.values || axis.values.length === 0) return false;
  return axis.values.every((value) => resolveShopColorSwatch(value) !== null);
}

/** Luminance relative WCAG d'un hex opaque `#rrggbb` (0 = noir, 1 = blanc). */
function relativeLuminance(hex: string): number {
  const clean = hex.replace("#", "");
  const channels = [0, 2, 4].map((offset) => {
    const raw = Number.parseInt(clean.slice(offset, offset + 2), 16) / 255;
    return raw <= 0.03928 ? raw / 12.92 : ((raw + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0]! + 0.7152 * channels[1]! + 0.0722 * channels[2]!;
}

/**
 * Encre de la coche de sélection : calculée, jamais « blanc » en dur — sur une
 * pastille blanche ou jaune une coche blanche est purement invisible.
 * Seuil 0.5 : au-dessus l'aplat est clair → encre foncée, et inversement.
 */
export function swatchInk(swatch: ShopColorSwatch): string {
  // Sur une BICOLORE, la coche est posée au centre, donc à cheval sur les deux
  // moitiés : aucune encre unie n'y est lisible des deux côtés (« noir et
  // blanc » est le pire cas). On arbitre alors sur la luminance moyenne, et
  // c'est `swatchInkHalo()` qui rend la coche lisible en la détourant.
  const lum = swatch.hex2
    ? (relativeLuminance(swatch.hex) + relativeLuminance(swatch.hex2)) / 2
    : relativeLuminance(swatch.hex);
  return lum > 0.5 ? "#111111" : "#ffffff";
}

/**
 * Détourage de la coche, ou `null` si inutile.
 *
 * Uniquement pour les BICOLORES : la coche y chevauche deux aplats de
 * luminances opposées, et disparaît donc forcément sur l'un des deux. Un liseré
 * de la couleur COMPLÉMENTAIRE de l'encre la rend lisible partout. Sur une
 * couleur unie l'encre calculée suffit — pas de détourage, qui salirait le
 * rendu.
 *
 * Rendu par `text-shadow` en aplats pleins : aucune opacité (règle
 * `ui_no_bg_transparency`).
 */
export function swatchInkHalo(swatch: ShopColorSwatch): string | null {
  if (!swatch.hex2) return null;
  return swatchInk(swatch) === "#ffffff" ? "#111111" : "#ffffff";
}

/**
 * Seuil de luminance au-delà duquel un aplat ne se détache plus d'un fond
 * blanc. Calibré sur les couleurs RÉELLES du dictionnaire : blanc 1.00,
 * ivoire 0.96, crème 0.86, écru 0.82, jaune pâle 0.75, sable 0.66, beige 0.62,
 * gris clair 0.61 — tous invisibles ou presque sur la carte blanche de la fiche
 * et donc concernés ; gris 0.30, jaune 0.58 et bleu ciel 0.54 se détachent
 * seuls. 0.55 est la coupure qui sépare ces deux groupes.
 */
const LIGHT_SWATCH_LUMINANCE = 0.55;

/**
 * L'aplat est-il trop clair pour se détacher d'un fond blanc ? Une pastille
 * « Blanc » ou « Beige » sans bordure est littéralement invisible sur la carte
 * blanche de la fiche produit. Au-dessus du seuil, la pastille reçoit une
 * bordure grise marquée au lieu d'une bordure discrète.
 *
 * On arbitre sur la moitié la PLUS CLAIRE d'une bicolore : c'est elle qui
 * disparaît, et une bordure marquée sur tout le cercle règle le cas.
 */
export function swatchNeedsOutline(swatch: ShopColorSwatch): boolean {
  const lum = Math.max(
    relativeLuminance(swatch.hex),
    swatch.hex2 ? relativeLuminance(swatch.hex2) : 0,
  );
  return lum > LIGHT_SWATCH_LUMINANCE;
}
